/* eslint-disable react/require-default-props */
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ContentSection, Fieldset, Heading, Layout, PageWrap, SectionHeading } from '@vwfs-bronson/bronson-react';
import { Formik, FormikHelpers, useFormikContext } from 'formik';
import { ObjectSchema } from 'yup';
import { getBrandName } from '../../../config';
import { entityLegalFormCodeToEntityLegalType } from '../../../resources/base/models/entityLegalFormMapping';
import { EntityLegalFormGroup, FinancialProductType } from '../../../resources/base/models/enums';
import { getCustomer } from '../../../services/api/miles/miles-customer-services';
import {
  calculateQuote,
  createOrUpdateBusinessCustomer,
  searchCustomerData,
} from '../../../services/api/miles/miles-services';
import { MilesCustomerSearchResponse } from '../../../services/api/miles/types';
import { readSiren } from '../../../services/api/readSiren/readSiren';
import { normalizeDate } from '../../../services/common/format';
import { getIsSoleTrader, strBoolToBool, waitForMiles } from '../../../services/common/miscUtils';
import { SetState } from '../../../services/common/miscUtils.types';
import { RootState } from '../../../services/redux';
import {
  customerSelectors,
  setCurrentCustomerId,
  setMissingMilesData,
} from '../../../services/redux/features/customer.slice';
import { loadingActions } from '../../../services/redux/features/loading.slice';
import { quoteSelectors } from '../../../services/redux/features/quote.slice';
import { useNavigate } from '../../../services/routing/navigate';
import { BusinessCustomerData } from '../../../types';
import { mapSalutationToGender } from '../utils/checkGenderUtil';
import { validateEmailTls } from '../utils/checkTLSUtil';
import {
  APECode,
  CarToBeReturned,
  CompanyCreationDate,
  CompanyName,
  CountryCodeMobilePhone,
  DateOfBirth,
  FirstName,
  Gender,
  LastName,
  LegalEntity,
  MobilePhone,
  PreviousContractNumber,
  PrivateUserCheck,
  SIRENWebserviceCall,
  SIRENWebserviceCallButton,
  TelephonePhone,
} from '../utils/fieldsBusinessCustomer';
import { ConsentModal } from './ConsentModal';
import { Email, IsExistingClient } from './fieldset';
import { BusinessCustomerProspectForm, getBusinessCustomerFormInitialValues } from './initialValues';
import { ProspectPageButtons } from './ProspectPageButtons';
import { getCreateOrUpdateBusinessCustomerRequest } from './utils';
import { getBusinessCustomerValidationSchema } from './validationSchema';

interface Props {
  initialMilesCustomerId: string | undefined;
  quoteId: string;
}

export const BusinessProspectForm: FunctionComponent<Props> = ({ initialMilesCustomerId, quoteId }) => {
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [initialValues, setInitialValues] = useState({} as BusinessCustomerProspectForm);
  const [validationSchema, setValidationSchema] = useState<ObjectSchema<BusinessCustomerProspectForm>>();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [milesCustomerId, setMilesCustomerId] = useState<string | undefined>(() => initialMilesCustomerId);
  const [isCompanyClosed, setIsCompanyClosed] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const quote = useSelector(quoteSelectors.selectQuote);
  const missingMilesData = useSelector(customerSelectors.selectMissingMilesData);
  const isQuoteEditable = useSelector(quoteSelectors.selectIsQuoteEditable);
  const existingCustomerEntityLegalFormGroup = useSelector(
    customerSelectors.selectExistingCustomerEntityLegalFormGroup
  );

  useEffect(() => {
    if (isSubmitted) return;

    dispatch(loadingActions.setLoading(true));
    (async () => {
      let customerData = {} as CustomerIntegration.Schemas.CustomerDataByIdResponse;

      if (milesCustomerId) {
        customerData = (await getCustomer(milesCustomerId)).customerData;
      }
      setInitialValues(getBusinessCustomerFormInitialValues(quote, customerData, missingMilesData));
      dispatch(loadingActions.setLoading(false));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isQuoteEditable) {
      const isSoleTrader = existingCustomerEntityLegalFormGroup === EntityLegalFormGroup.SoleTrader;
      setValidationSchema(getBusinessCustomerValidationSchema(isSoleTrader, t));
    } else {
      setValidationSchema(undefined);
    }
  }, [t, existingCustomerEntityLegalFormGroup, isQuoteEditable]);

  const onSubmit = async (
    values: BusinessCustomerProspectForm,
    { setFieldError }: FormikHelpers<BusinessCustomerProspectForm>
  ) => {
    const tlsValid = await validateEmailTls(values.email!, setFieldError, dispatch);

    if (!tlsValid) {
      return;
    }

    if (!isQuoteEditable) {
      navigate.form('/3');
      return;
    }

    dispatch(loadingActions.setLoading(true));

    try {
      const calculateQuoteRequest: QuoteSchema.Schemas.CalculateSalesQuoteRequest = {};
      const createUpdateBusinessRequest = getCreateOrUpdateBusinessCustomerRequest(values, milesCustomerId);

      const newMilesCustomerId = (await createOrUpdateBusinessCustomer(createUpdateBusinessRequest, quoteId))
        .milesCustomerId;

      const { companyData, financialProduct } = createUpdateBusinessRequest;

      calculateQuoteRequest.financialProduct = financialProduct;
      calculateQuoteRequest.customerData = {
        companyData: {
          companyCarForPrivateUse: companyData?.privateUserCheck,
        },
      };

      calculateQuoteRequest.financialProduct = financialProduct;

      await waitForMiles();
      await calculateQuote(quoteId, calculateQuoteRequest);

      dispatch(setCurrentCustomerId(newMilesCustomerId));
      dispatch(setMissingMilesData({ hasCoCustomer: false }));
      setIsSubmitted(true);

      setShowConsentModal(true);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      navigate.error();
    } finally {
      dispatch(loadingActions.setLoading(false));
    }
  };

  return (
    <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      <PageWrap>
        <ContentSection altBackground={getBrandName().includes('vw')} className="u-pv-none">
          {/* eslint-disable-next-line no-use-before-define */}
          <FormContent
            existingCustomerEntityLegalFormGroup={existingCustomerEntityLegalFormGroup}
            setMilesCustomerId={setMilesCustomerId}
            milesCustomerId={milesCustomerId}
            isCompanyClosed={isCompanyClosed}
            setIsCompanyClosed={setIsCompanyClosed}
          />
          <Layout center>
            <ProspectPageButtons isCompanyClosed={isCompanyClosed} />
          </Layout>
        </ContentSection>
        <ConsentModal
          visible={showConsentModal}
          setShowConsentModal={setShowConsentModal}
          isCoCustomer={false}
          hasCoCustomer={false}
        />
      </PageWrap>
    </Formik>
  );
};

interface FormContentProps {
  existingCustomerEntityLegalFormGroup: EntityLegalFormGroup;
  milesCustomerId: string | undefined;
  setMilesCustomerId?: React.Dispatch<React.SetStateAction<string | undefined>>;
  isCompanyClosed: boolean;
  setIsCompanyClosed: SetState<boolean>;
}

const FormContent: FunctionComponent<FormContentProps> = ({
  setMilesCustomerId,
  isCompanyClosed,
  setIsCompanyClosed,
}) => {
  const { t } = useTranslation();
  const { values, setFieldValue, setFieldTouched } = useFormikContext<BusinessCustomerProspectForm>();
  const [showSoleTraderWarning, setShowSoleTraderWarning] = useState(false);

  const isCompanyClosedRef = useRef(isCompanyClosed);
  useEffect(() => {
    isCompanyClosedRef.current = isCompanyClosed;
  }, [isCompanyClosed]);

  const dispatch = useDispatch();

  const selectedProductType = useSelector((state: RootState) => state.productType.selectedProductType);

  const isQuoteEditable = useSelector(quoteSelectors.selectIsQuoteEditable);

  const updateFieldValues = (fields: Partial<BusinessCustomerData>) => {
    Object.entries(fields).forEach(([key, value]) => {
      setFieldValue(key, value, false);
      setFieldTouched(key, false, false);
    });
  };

  const extractBusinessCustomerData = (businessCustomerData: MilesCustomerSearchResponse) => {
    const firstBusinessCustomerData = businessCustomerData?.customers?.[0]?.customerData || {};

    return {
      milesCustomerId: firstBusinessCustomerData?.externalIdentifiers?.find((id) => id.type === 'MILES_CUSTOMER_ID')
        ?.id,
      mobilePhoneCountryCode: firstBusinessCustomerData?.contactData?.mobilePhoneCountryCode || '+33',
      mobilePhone: firstBusinessCustomerData?.contactData?.mobilePhoneNumber || '',
      telephonePhone: firstBusinessCustomerData?.contactData?.telephoneNumber || '',
      email: firstBusinessCustomerData?.contactData?.emails?.[0]?.emailAddress || '',
      gender: mapSalutationToGender(firstBusinessCustomerData?.salutation),
      firstName: firstBusinessCustomerData?.firstName || '',
      lastName: firstBusinessCustomerData?.lastName || '',
      tradingName: firstBusinessCustomerData?.tradingName || '',
      dateOfBirth: normalizeDate(firstBusinessCustomerData?.dateOfBirth) || '',
    };
  };

  const setSirenData = async () => {
    dispatch(loadingActions.setLoading(true));

    try {
      const sirenData = await readSiren(values.sirenWebserviceCall);
      setIsCompanyClosed(sirenData?.companyStatus === 'terminated');
      const businessCustomerData = await searchCustomerData({ siren: values?.sirenWebserviceCall }, 1);
      const customerData = extractBusinessCustomerData(businessCustomerData);
      const legalEntityValue = sirenData?.entityLegalFormCode
        ? entityLegalFormCodeToEntityLegalType[sirenData.entityLegalFormCode]
        : '';
      setMilesCustomerId?.(customerData.milesCustomerId);
      const isSoleTrader = getIsSoleTrader(legalEntityValue);
      const isNewCustomer = !customerData.milesCustomerId;
      setShowSoleTraderWarning(isSoleTrader && isNewCustomer);

      const updatedValues: Partial<BusinessCustomerData> = {
        companyName: sirenData?.name || customerData.tradingName,
        apecode: sirenData?.activityCode,
        companyCreationDate: normalizeDate(sirenData?.foundationDate),
        legalEntity: legalEntityValue,
        ...customerData,
      };
      updateFieldValues(updatedValues);
    } finally {
      dispatch(loadingActions.setLoading(false));
    }
  };

  useEffect(() => {
    dispatch(setMissingMilesData({ siren: values.sirenWebserviceCall }));
    if (isCompanyClosedRef.current) {
      updateFieldValues({
        companyName: '',
        apecode: '',
        companyCreationDate: '',
        legalEntity: '',
      });
      setShowSoleTraderWarning(false);
    }
    setIsCompanyClosed(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.sirenWebserviceCall]);

  useEffect(() => {
    dispatch(setMissingMilesData({ legalEntity: values.legalEntity }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.legalEntity]);

  return (
    <ContentSection.ComponentWrapper>
      <SectionHeading level={2}>{t('step2:heading:businessCustomer')}</SectionHeading>
      <Fieldset disabled={!isQuoteEditable}>
        <Fieldset.Row className="u-mb-none">
          <Layout>
            <Layout.Item default="1/2" s="1/1">
              <SIRENWebserviceCall t={t} />
            </Layout.Item>
            <Layout.Item default="1/2" s="1/1">
              <SIRENWebserviceCallButton
                t={t}
                onClick={setSirenData}
                isDisabled={!values.sirenWebserviceCall || !isQuoteEditable}
              />
            </Layout.Item>
            {showSoleTraderWarning && (
              <Layout.Item default="1/1" s="1/1">
                <p className="u-mt-none u-mb-none u-font-size-fs-1" style={{ fontStyle: 'italic' }}>
                  {t('step2:soleTraderSirenWarning')}
                </p>
              </Layout.Item>
            )}
            {isCompanyClosed && (
              <Layout.Item default="1/1" s="1/1">
                <p
                  className="u-mt-none u-mb-none u-font-size-fs-1 c-error-message"
                  id="closed-company-error"
                  style={{ fontStyle: 'italic' }}
                >
                  {t('step2:closedCompanyError')}
                </p>
              </Layout.Item>
            )}
            <Layout.Item default="1/2" s="1/1" />
          </Layout>
        </Fieldset.Row>
        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">
              <LegalEntity t={t} setFieldValue={setFieldValue} values={values} />
            </Layout.Item>
            <Layout.Item default="1/2" s="1/1">
              <CompanyName t={t} />
            </Layout.Item>
            <Layout.Item default="1/2" s="1/1">
              <APECode t={t} />
            </Layout.Item>
            <Layout.Item default="1/2" s="1/1">
              <CompanyCreationDate
                t={t}
                setFieldValue={setFieldValue}
                values={values}
                label={t('step4:companyCreationDate:label')}
                infoText={t('step4:dateInfoText:label')}
              />
            </Layout.Item>
            {getIsSoleTrader(values.legalEntity) ? (
              <>
                <Layout.Item default="1/2" s="1/1">
                  <Gender t={t} />
                </Layout.Item>
                <Layout.Item default="1/2" s="1/1">
                  <FirstName isIndividualEntrepreneur setFieldValue={setFieldValue} t={t} />
                </Layout.Item>
                <Layout.Item default="1/2" s="1/1">
                  <LastName isIndividualEntrepreneur setFieldValue={setFieldValue} t={t} />
                </Layout.Item>
                <Layout.Item default="1/2" s="1/1">
                  <DateOfBirth
                    t={t}
                    setFieldValue={setFieldValue}
                    values={values}
                    label={t('step4:dateOfBirth:label')}
                    infoText={t('step2:dateInfoText:label')}
                  />
                </Layout.Item>
                {/* TODO - uncomment once BFT supports soletrader birthName */}
                {/* <Layout.Item default="1/2" s="1/1">
                  <BirthName t={t} />
                </Layout.Item> */}
              </>
            ) : null}
            {selectedProductType === FinancialProductType.FINANCING && (
              <Layout.Item default="1/1" s="1/1">
                <PrivateUserCheck t={t} setFieldValue={setFieldValue} values={values} />
              </Layout.Item>
            )}
            <Layout.Item default="1/2" s="1/1">
              <CountryCodeMobilePhone t={t} setFieldValue={setFieldValue} />
            </Layout.Item>
            <Layout.Item default="1/2" s="1/1">
              <MobilePhone t={t} setFieldValue={setFieldValue} values={values} />
            </Layout.Item>
            <Layout.Item default="1/2" s="1/1">
              <TelephonePhone t={t} setFieldValue={setFieldValue} />
            </Layout.Item>
            <Layout.Item default="1/2" s="1/1">
              <Email />
            </Layout.Item>
            <Layout.Item default="1/2" s="1/1" />
          </Layout>
        </Fieldset.Row>
        {selectedProductType === FinancialProductType.FINANCING && (
          <>
            <Heading level={6}>{t('step2:heading:typeOfOffer')}</Heading>
            <Fieldset.Row>
              <Layout>
                <Layout.Item default="1/2" s="1/1">
                  <IsExistingClient />
                </Layout.Item>
                {strBoolToBool(values.isExistingClient) && (
                  <>
                    <Layout.Item default="1/2" s="1/1" />
                    <Layout.Item default="1/2" s="1/1">
                      <PreviousContractNumber t={t} />
                    </Layout.Item>
                    <Layout.Item default="1/2" s="1/1">
                      <CarToBeReturned t={t} />
                    </Layout.Item>
                  </>
                )}
              </Layout>
            </Fieldset.Row>
          </>
        )}
      </Fieldset>
    </ContentSection.ComponentWrapper>
  );
};
